import { Grid, Typography } from "@mui/material";
import { AddSharp } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LocalStorage from "stores/LocalStorage";
import { useStore } from "utils/useStore";
import useOrganizationAnalystGroups from "../../hooks/useOrganizationAnalystGroups";
import useOrganizationUsers from "../../hooks/useOrganizationUsers";
import { AnalystGroup } from "../../types/AnalystGroup";
import OrganizationAnalystGroupsDetail from "./OrganizationAnalystGroupsDetail";
import OrganizationAnalystGroupsListCards from "./OrganizationAnalystGroupsListCards";
import OrganizationAnalystGroupsListSearch from "./OrganizationAnalystGroupsListSearch";
import OrganizationAnalystGroupsListTable from "./OrganizationAnalystGroupsListTable";
import OrganizationAnalystGroupsListViewSelect from "./OrganizationAnalystGroupsListViewSelect";

const lsViewTypeKey = "AnalystGroupsListViewType";

export enum ViewType {
    cardView = "cardView",
    listView = "listView",
}

type Props = {
    orgId: string;
};

const OrganizationAnalystGroupsList: React.FC<Props> = ({ orgId }) => {
    const location = useLocation();
    const { getLocalStore } = useStore(LocalStorage);
    const ls = getLocalStore("AcxDefault");
    const { users, isLoading: isLoadingUsers } = useOrganizationUsers(orgId);
    const { analystGroups, isLoading, refetch } =
        useOrganizationAnalystGroups(orgId);
    const [filter, setFilter] = useState<string>("");
    const navigate = useNavigate();
    const [filteredAnalystGroups, setFilteredAnalystGroups] = useState<
        AnalystGroup[]
    >([]);
    const [detailData, setDetailData] = useState<AnalystGroup>({
        groupName: "",
        description: "",
        organizationStructureMemberIds: [],
        members: [],
    });
    const [view, setView] = useState<ViewType>();
    // const { id } = qs.parse(search);
    const params = new URLSearchParams(location.search);
    const id = params.get("id");

    useEffect(() => {
        let destroyed = false;
        const getLsData = async () => {
            const viewType: ViewType | null = await ls.getItem(lsViewTypeKey);
            console.log({ viewType });
            if (!destroyed) {
                setView(viewType || ViewType.cardView);
            }
        };
        getLsData();
        return () => {
            destroyed = true;
        };
    }, [ls]);

    useEffect(() => {
        if (!isLoadingUsers) {
            const analystGroup = analystGroups?.find(
                (group) => group.id === id,
            );
            const analystGroupToUse = {
                ...analystGroup,
                members: analystGroup?.members?.map((member) => {
                    const foundUser = users?.find(
                        (user) => user.id === member.userId,
                    );
                    return {
                        ...member,
                        name: foundUser?.userName || "",
                    };
                }),
            } as AnalystGroup;
            setDetailData({
                ...{
                    groupName: "",
                    description: "",
                    organizationStructureMemberIds: [],
                    members: [],
                },
                ...analystGroupToUse,
            });
        }
    }, [id, analystGroups, isLoadingUsers, users]);

    useEffect(() => {
        const filtered = analystGroups?.filter((group) =>
            group.$search?.includes(filter),
        );
        setFilteredAnalystGroups(filtered ? [...filtered] : []);
    }, [analystGroups, filter]);

    const changeView = async (view: ViewType) => {
        setView(view);
        await ls.setItem(lsViewTypeKey, view);
    };

    const onCloseDetail = () => {
        navigate(`/admin/organizations/${orgId}/analyst-groups`);
    };

    return (
        <>
            {view ? (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        padding: 0,
                        position: "relative",
                        width: 424,
                        height: 400,
                        filter: "drop-shadow(0px 25px 50px rgba(0, 0, 0, 0.25))",
                        borderRadius: 8,
                    }}
                >
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems={"flex-end"}
                        sx={{
                            marginTop: 2,
                            marginBottom: 2,
                            alignItems: "center",
                        }}
                    >
                        <Grid item xs={3}>
                            <Typography variant="subtitle1">
                                {!!filteredAnalystGroups.length && (
                                    <>{filteredAnalystGroups.length} </>
                                )}
                                Analyst Groups
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={9}
                            container
                            spacing={1}
                            justifyContent="flex-end"
                            alignItems={"flex-end"}
                            alignContent="flex-end"
                            sx={{ margin: 0 }}
                        >
                            <div style={{ display: "flex" }}>
                                <OrganizationAnalystGroupsListSearch
                                    setFilter={setFilter}
                                />
                                <OrganizationAnalystGroupsListViewSelect
                                    view={view}
                                    changeView={changeView}
                                />
                                <AcxButton
                                    fullWidth
                                    color="primary"
                                    startIcon={<AddSharp />}
                                    onClick={() => {
                                        navigate(
                                            `/admin/organizations/${orgId}/analyst-groups?id=add`,
                                        );
                                    }}
                                >
                                    New Group
                                </AcxButton>
                            </div>
                        </Grid>
                    </Grid>
                    {view === ViewType.cardView ? (
                        <OrganizationAnalystGroupsListCards
                            orgId={orgId}
                            data={filteredAnalystGroups}
                            isLoading={isLoading}
                        />
                    ) : (
                        <OrganizationAnalystGroupsListTable
                            orgId={orgId}
                            data={filteredAnalystGroups}
                            isLoading={isLoading}
                        />
                    )}
                </div>
            ) : (
                <></>
            )}

            {((id && detailData) || (id && id === "add")) && !isLoadingUsers && (
                <OrganizationAnalystGroupsDetail
                    orgId={orgId}
                    id={id}
                    close={onCloseDetail}
                    refetch={refetch}
                    data={detailData}
                    memberOptions={users?.map((user) => ({
                        type: "Manager",
                        userId: user.id || "",
                        name: user.userName,
                    }))}
                    isLoadingUsers={isLoadingUsers}
                />
            )}
        </>
    );
};

export default OrganizationAnalystGroupsList;
